<template>
  <div>
    <action-menu-wrapper :items="options" :offset="offset" teleport />
    <confirm-modal
      v-if="showDeleteModal"
      :title="
        $t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_TITLE', {
          type: typeMap[type].title,
        })
      "
      :description="
        $t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_DESCRIPTION', {
          name: model.name,
        })
      "
      :confirm-button="$t('COMMON.DELETE')"
      confirm-button-type="error"
      :close="() => (showDeleteModal = false)"
      @confirm="handleDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import pick from "lodash/pick";

import { CommunicationType } from "@/enums/communicationLogs";
import { EmailTemplateVisibility } from "@/enums/emailCustomization";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import {
  ROUTE_EMAIL_TEMPLATES_EDIT,
  ROUTE_SMS_TEMPLATES_EDIT,
} from "@/router/routes";
import customizationApiService from "@/services/modules/customization";

import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper from "@/components/ui/ActionMenuWrapperV2.vue";

import type { MenuItem } from "@/components/ui/ActionMenuWrapperV2.vue";
import type { CommunicationTemplate, EmailTemplate } from "@/models/clients";

interface Props {
  model: CommunicationTemplate;
  type: CommunicationType.email | CommunicationType.sms;
}

const emit = defineEmits<{
  "template:deleted": [];
  "template:duplicated": [];
  "template:updated": [];
}>();

const { model, type } = defineProps<Props>();

const { t } = useI18n();
const router = useRouter();
const { isClientAdmin, isAdmin } = useAuth();
const { activeClient } = useClients();
const showDeleteModal = ref(false);

const typeMap = {
  [CommunicationType.email]: {
    duplicate: customizationApiService.duplicateEmailTemplate,
    delete: customizationApiService.deleteEmailTemplate,
    update: customizationApiService.updateEmailTemplate,
    route: ROUTE_EMAIL_TEMPLATES_EDIT,
    title: t("ORGANIZATION.COMMUNICATION_TEMPLATES.EMAIL_CUSTOMIZATION"),
  },
  [CommunicationType.sms]: {
    duplicate: customizationApiService.duplicateSmsTemplate,
    delete: customizationApiService.deleteSmsTemplate,
    update: customizationApiService.updateSmsTemplate,
    route: ROUTE_SMS_TEMPLATES_EDIT,
    title: t("ORGANIZATION.COMMUNICATION_TEMPLATES.SMS.CUSTOMIZATION"),
  },
};

const typeMapKey = computed(() => typeMap[type]);
const widthOffset = computed(() =>
  model?.visibility === EmailTemplateVisibility.Organizational ? -118 : -154
);
const offset = computed(() => ({ left: widthOffset.value, top: 5 }));

const options = computed<Array<MenuItem>>(() => [
  { label: t("COMMON.EDIT"), handler: handleEdit },
  { label: t("COMMON.DUPLICATE"), handler: handleDuplicate },
  ...getVisibilityOptions(),
  { label: t("COMMON.DELETE"), handler: () => (showDeleteModal.value = true) },
]);

async function handleEdit() {
  const clientId = activeClient.value?.id;
  if (!model?.id) {
    return;
  }
  const pushPayload = {
    name: typeMapKey.value.route,
    params: {
      id: clientId,
      templateId: model.id,
    },
  };

  await router.push(pushPayload);
}

const getVisibilityOptions = () => {
  const isAdminRole = isClientAdmin || isAdmin;

  const options: MenuItem[] = [];

  if (!isAdminRole) {
    return options;
  }

  const organizationalOption = {
    label: t("ORGANIZATION.COMMUNICATION_TEMPLATES.SET_AS_ORGANIZATIONAL"),
    handler: () =>
      handleUpdate({ visibility: EmailTemplateVisibility.Organizational }),
  };

  const globalOption = {
    label: t("ORGANIZATION.COMMUNICATION_TEMPLATES.SET_AS_GLOBAL"),
    handler: () => handleUpdate({ visibility: EmailTemplateVisibility.Global }),
  };

  const personalOption = {
    label: t("ORGANIZATION.COMMUNICATION_TEMPLATES.SET_AS_PERSONAL"),
    handler: () =>
      handleUpdate({ visibility: EmailTemplateVisibility.Personal }),
  };

  if (model.visibility !== EmailTemplateVisibility.Personal) {
    options.push(personalOption);
  }

  if (model.visibility !== EmailTemplateVisibility.Organizational) {
    options.push(organizationalOption);
  }

  if (model.visibility !== EmailTemplateVisibility.Global && isAdmin) {
    options.push(globalOption);
  }

  return options;
};

const { fetchWrapper: handleDuplicate } = usePromiseWrapper(async () => {
  if (!model.id) {
    return;
  }
  await typeMapKey.value.duplicate(model.id);
  emit("template:duplicated");
});

const { fetchWrapper: handleDelete } = usePromiseWrapper(async () => {
  if (!model.id) {
    return;
  }
  await typeMapKey.value.delete(model.id);
  emit("template:deleted");
});

const { fetchWrapper: handleUpdate } = usePromiseWrapper(
  async (updateValues: Partial<CommunicationTemplate>) => {
    if (!model.id) {
      return;
    }

    const payload = {
      ...pick(model, ["id", "name", "content"]),
      ...(type === CommunicationType.email && {
        subject: (model as EmailTemplate).subject,
      }),
      visibility: updateValues.visibility,
    };
    await typeMapKey.value.update(payload);
    emit("template:updated");
  }
);
</script>
